import {useCallback, useRef, useState} from 'react';
import {AiChunk, ProjectDataIndex} from '../../api/types.ts';
import useCreateImageFromPrompt from '../../api/assistant/useCreateImageFromPrompt.ts';
import {useMutation, useQuery} from 'react-query';
import {toast} from 'react-toastify';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import getTimezoneOffset from '../../lib/getTimezoneOffset.ts';
import useGetDailyAiImageCreationUsage from '../../api/assistant/useGetDailyAiImageCreationUsage.ts';
import useAiHub from './useAiHub.ts';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';

// Field keys pushed by the server (see CreateImageFromPrompt.cs).
const IMAGE_URL_FIELD_KEY = 'imageUrl';
const ERROR_FIELD_KEY = 'error';

const useImageCreator = (
    projectId: string | null,
    onImageGenerated?: (imageUrl: string) => void
) => {
    const {loggedInUserId} = useAuthStateManager();
    const timezoneOffset = getTimezoneOffset();
    const getDailyAiImageCreationUsage = useGetDailyAiImageCreationUsage({
        timezoneOffset
    });

    const {
        data: dailyAiImageCreationUsage,
        refetch: refetchDailyAiImageCreationUsage
    } = useQuery(
        ['daily-ai-image-creation-usage', timezoneOffset],
        getDailyAiImageCreationUsage
    );

    const dailyLimit =
        dailyAiImageCreationUsage?.dailyAiImageCreationLimit ?? 0;
    const imageCount =
        dailyAiImageCreationUsage?.dailyAiImageCreationCount ?? 0;
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);

    // Generation runs as a background job on the server; the result (or error)
    // arrives over the AiHub group, not in the HTTP response.
    const groupName =
        projectId && loggedInUserId
            ? `ImageGen_${projectId}_${loggedInUserId}`
            : null;

    // Keep the callback identity stable so useAiHub doesn't reconnect on every
    // render of the calling component.
    const onImageGeneratedRef = useRef(onImageGenerated);
    onImageGeneratedRef.current = onImageGenerated;

    const onChunkGenerated = useCallback((aiChunk: AiChunk) => {
        if (aiChunk.fieldKey === IMAGE_URL_FIELD_KEY) {
            toast.success('Image generated');
            if (onImageGeneratedRef.current) {
                onImageGeneratedRef.current(aiChunk.chunk);
            }
        } else if (aiChunk.fieldKey === ERROR_FIELD_KEY) {
            toast.error(aiChunk.chunk);
        }
    }, []);

    const onComplete = useCallback(() => {
        setIsGeneratingImage(false);
        refetchDailyAiImageCreationUsage();
    }, [refetchDailyAiImageCreationUsage]);

    useAiHub({groupName, onChunkGenerated, onComplete});

    const createImageFromPrompt = useCreateImageFromPrompt();

    const createImageFromPromptMutation = useMutation(createImageFromPrompt, {
        onMutate: () => {
            setIsGeneratingImage(true);
        },
        onError: (error: unknown) => {
            toast.error(unknownErrorToString(error));
            setIsGeneratingImage(false);
        }
    });

    const generateImage = async (
        prompt: string,
        mixId: string,
        dataIndex: ProjectDataIndex,
        useAsThumbnail: boolean
    ): Promise<void> => {
        if (imageCount < dailyLimit) {
            try {
                await createImageFromPromptMutation.mutateAsync({
                    id: mixId,
                    dataIndex,
                    useAsThumbnail,
                    prompt,
                    timezoneOffset: getTimezoneOffset()
                });

                toast.success(
                    'Image request submitted - it may take a few minutes for the image to show in your project.'
                );
            } catch (error) {
                console.error('Error generating image:', error);
            }
        }
    };

    return {imageCount, generateImage, dailyLimit, isGeneratingImage};
};

export default useImageCreator;
